import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Calendar, 
  MessageSquare, 
  Settings, 
  BarChart, 
  Users,
  Code,
  Menu,
  X,
  LogOut,
  ChevronDown,
  User
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
const sidebarItems = [
  { 
    icon: LayoutDashboard, 
    label: 'Overview', 
    path: '/dashboard' 
  },
  { 
    icon: Calendar, 
    label: 'Appointments', 
    path: '/dashboard/appointments' 
  },
  { 
    icon: MessageSquare, 
    label: 'Communications', 
    path: '/dashboard/communications' 
  },
  { 
    icon: Users, 
    label: 'Customers', 
    path: '/dashboard/customers' 
  },
  { 
    icon: BarChart, 
    label: 'Analytics', 
    path: '/dashboard/analytics' 
  },
  { 
    icon: Code, 
    label: 'Integration', 
    path: '/dashboard/integration' 
  },
  { 
    icon: Settings, 
    label: 'Settings', 
    path: '/dashboard/settings' 
  },
];
const ProfileDropdown = ({ user, onLogout }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
  
    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      };
  
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
  
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100"
        >
          <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
            <User size={20} className="text-primary-600" />
          </div>
          <span className="text-sm font-medium text-gray-700">{user?.businessName}</span>
          <ChevronDown size={16} className="text-gray-500" />
        </button>
  
        {isOpen && (
          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 border">
            <div className="px-4 py-2 border-b">
              <p className="text-sm font-medium text-gray-900">{user?.businessName}</p>
              <p className="text-xs text-gray-500">{user?.email}</p>
            </div>
            <button
              onClick={onLogout}
              className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50"
            >
              <LogOut size={16} className="mr-2" />
              Logout
            </button>
          </div>
        )}
      </div>
    );
  };
  
  
const DashboardLayout = ({ children }) => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
  
    const handleLogout = () => {
      logout();
      navigate('/login');
    };
  

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  return (
    <div className="min-h-screen flex bg-gray-100">
      {/* Sidebar */}
      <div className={`
        fixed top-0 left-0 h-full bg-white shadow-lg transition-all duration-300 z-20
        ${isSidebarOpen ? 'w-64' : 'w-20'}
      `}>
        <div className="flex items-center justify-between h-16 px-4 border-b">
          <Link to="/dashboard" className="flex items-center">
            <span className={`font-bold text-xl ${!isSidebarOpen && 'hidden'}`}>
              LocalFlow
            </span>
          </Link>
          <button onClick={toggleSidebar} className="p-2 rounded-lg hover:bg-gray-100">
            {isSidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
        
        <nav className="p-4">
          {sidebarItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`
                flex items-center p-3 mb-2 rounded-lg transition-colors
                ${location.pathname === item.path 
                  ? 'bg-primary-50 text-primary-600' 
                  : 'hover:bg-gray-100'
                }
              `}
            >
              <item.icon size={20} />
              <span className={`ml-3 ${!isSidebarOpen && 'hidden'}`}>
                {item.label}
              </span>
            </Link>
          ))}
        </nav>
      </div>

      {/* Main Content */}
      <div className={`
        flex-1 transition-all duration-300
        ${isSidebarOpen ? 'ml-64' : 'ml-20'}
      `}>
        <header className="h-16 bg-white border-b px-6 flex items-center justify-between">
          <h1 className="text-xl font-semibold">
            {sidebarItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
          </h1>
          
          {/* Profile dropdown can be added here */}
          <ProfileDropdown user={user} onLogout={handleLogout} />

        </header>

        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;